import { globalRadioStations, type GlobalRadioStation } from '@/mocks/globalRadios';

export interface RecommendationContext {
  category?: string;
  city?: string;
  state?: string;
  language?: string;
  excludeIds?: number[];
}

export interface RecommendedRadio {
  station: GlobalRadioStation;
  reason: string;
  score: number;
}

const reasonTemplates: Record<string, string[]> = {
  category: [
    'Misma categoría que escuchas',
    'Género similar a tus favoritos',
    'Estilo que te gusta',
  ],
  city: [
    'De tu misma ciudad',
    'Cerca de tu ubicación',
    'En tu área local',
  ],
  state: [
    'De tu mismo estado',
    'En tu región',
    'Cerca de ti',
  ],
  language: [
    'En tu idioma preferido',
    'Habla tu mismo idioma',
    'Contenido en Español',
  ],
  popular: [
    'Popular entre oyentes',
    'En tendencia ahora',
    'Altamente recomendada',
  ],
};

export function generateRecommendations(
  context: RecommendationContext,
  limit: number = 6
): RecommendedRadio[] {
  const excludeIds = new Set(context.excludeIds || []);

  const scored = globalRadioStations
    .filter((station) => !excludeIds.has(station.id))
    .map((station) => {
      let score = 0;
      let reason = '';

      if (context.category && station.category === context.category) {
        score += 3;
        if (!reason) reason = reasonTemplates.category[Math.floor(Math.random() * reasonTemplates.category.length)];
      }

      if (context.state && station.state === context.state) {
        score += 2;
        if (!reason) reason = reasonTemplates.state[Math.floor(Math.random() * reasonTemplates.state.length)];
      }

      if (context.city && station.city === context.city) {
        score += 2;
        if (!reason) reason = reasonTemplates.city[Math.floor(Math.random() * reasonTemplates.city.length)];
      }

      if (context.language && station.language === context.language) {
        score += 1;
        if (!reason) reason = reasonTemplates.language[Math.floor(Math.random() * reasonTemplates.language.length)];
      }

      // Push live stations a bit higher
      if (station.isLive) {
        score += 1;
      }

      // Boost trending
      score += station.trendingScore / 100;

      if (!reason) {
        reason = reasonTemplates.popular[Math.floor(Math.random() * reasonTemplates.popular.length)];
      }

      return { station, reason, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored;
}

export function generateGenericRecommendations(limit: number = 8): RecommendedRadio[] {
  return globalRadioStations
    .sort((a, b) => b.trendingScore - a.trendingScore)
    .slice(0, limit)
    .map((station, i) => ({
      station,
      reason: reasonTemplates.popular[i % reasonTemplates.popular.length],
      score: station.trendingScore,
    }));
}

export function generateRecommendationsFromProfile(
  categories: string[],
  cities: string[],
  states: string[],
  excludeIds: number[],
  limit: number = 6
): RecommendedRadio[] {
  const excludeSet = new Set(excludeIds);

  const scored = globalRadioStations
    .filter((station) => !excludeSet.has(station.id))
    .map((station) => {
      let score = 0;
      let reason = '';

      if (categories.includes(station.category)) {
        score += 3;
        if (!reason) reason = reasonTemplates.category[Math.floor(Math.random() * reasonTemplates.category.length)];
      }

      if (states.includes(station.state)) {
        score += 2;
        if (!reason) reason = reasonTemplates.state[Math.floor(Math.random() * reasonTemplates.state.length)];
      }

      if (cities.includes(station.city)) {
        score += 2;
        if (!reason) reason = reasonTemplates.city[Math.floor(Math.random() * reasonTemplates.city.length)];
      }

      if (station.isLive) {
        score += 1;
      }

      score += station.trendingScore / 100;

      if (!reason) {
        reason = reasonTemplates.popular[Math.floor(Math.random() * reasonTemplates.popular.length)];
      }

      return { station, reason, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored;
}